import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { invitationService } from "../services/invitationService";
import { Invitation, InvitationStatus } from "../types/invitation";
import Header from "../components/layout/Header";
import { userService } from "../services/userService";
import { UserData } from "../types/user";

const Invitations = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingInvitations, setProcessingInvitations] = useState<
    Set<string>
  >(new Set());
  const [viewedUser, setViewedUser] = useState<UserData | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const viewingAsAdmin = searchParams.get("uid") !== null;

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        setLoading(true);
        setError(null);

        const targetUid = searchParams.get("uid");
        let emailToFetch: string;

        if (targetUid) {
          // Admin viewing another user's invitations
          const userData = await userService.getUserData(targetUid);
          if (!userData) {
            setError(`User with UID ${targetUid} not found`);
            setLoading(false);
            return;
          }
          setViewedUser(userData);
          emailToFetch = userData.email;
          console.log(
            `[Admin View] Fetching invitations for user: ${userData.name} ${userData.surname} (${emailToFetch})`
          );
        } else {
          // Normal user viewing their own invitations
          if (!currentUser?.email) {
            setLoading(false);
            return;
          }
          setViewedUser(null);
          emailToFetch = currentUser.email;
        }

        const userInvitations = await invitationService.getInvitations(
          emailToFetch
        );
        setInvitations(userInvitations);
        console.log(
          `Loaded ${userInvitations.length} invitations for ${emailToFetch}`
        );
      } catch (error: any) {
        console.error("Error fetching invitations:", error);
        setError(`Failed to load invitations: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchInvitations();
  }, [currentUser?.email, searchParams]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);

    try {
      const targetUid = searchParams.get("uid");
      let emailToFetch: string;

      if (targetUid) {
        const userData = await userService.getUserData(targetUid);
        if (!userData) {
          setError(`User with UID ${targetUid} not found`);
          return;
        }
        emailToFetch = userData.email;
      } else {
        if (!currentUser?.email) {
          return;
        }
        emailToFetch = currentUser.email;
      }

      const userInvitations = await invitationService.getInvitations(
        emailToFetch
      );
      setInvitations(userInvitations);
      console.log(
        `Refreshed: Loaded ${userInvitations.length} invitations for ${emailToFetch}`
      );
    } catch (error: any) {
      console.error("Error refreshing invitations:", error);
      setError(`Failed to load invitations: ${error.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    setProcessingInvitations((prev) => new Set(prev).add(invitationId));
    try {
      await invitationService.updateInvitationStatus(invitationId, "accepted");

      // Update local state to reflect the change
      setInvitations((prev) =>
        prev.map((inv) =>
          inv.id === invitationId
            ? { ...inv, status: "accepted" as InvitationStatus }
            : inv
        )
      );
    } catch (error) {
      console.error("Error accepting invitation:", error);
      setError("Failed to accept invitation");
    } finally {
      setProcessingInvitations((prev) => {
        const newSet = new Set(prev);
        newSet.delete(invitationId);
        return newSet;
      });
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    setProcessingInvitations((prev) => new Set(prev).add(invitationId));
    try {
      await invitationService.updateInvitationStatus(invitationId, "declined");

      // Update local state to reflect the change
      setInvitations((prev) =>
        prev.map((inv) =>
          inv.id === invitationId
            ? { ...inv, status: "declined" as InvitationStatus }
            : inv
        )
      );
    } catch (error) {
      console.error("Error declining invitation:", error);
      setError("Failed to decline invitation");
    } finally {
      setProcessingInvitations((prev) => {
        const newSet = new Set(prev);
        newSet.delete(invitationId);
        return newSet;
      });
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="w-full pt-16">
        <Header />
        <div className="p-6 max-w-4xl mx-auto">
          <h2 className="text-2xl !font-grotesk font-semibold text-base-content mb-4">
            Coach Invitations
          </h2>
          <div className="animate-pulse">
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-base-100 rounded-lg shadow p-4">
                  <div className="h-4 bg-base-300 rounded w-1/4 mb-2"></div>
                  <div className="h-3 bg-base-300 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full pt-10">
        <Header />
        <div className="p-6 max-w-4xl mx-auto">
          <h2 className="text-2xl !font-grotesk font-semibold text-base-content mb-4">
            Coach Invitations
          </h2>
          <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4">
            <p className="text-red-700 dark:text-red-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center justify-start pt-10 px-6">
      <Header />
      <div className="max-w-4xl w-full pt-12">
        {viewingAsAdmin && viewedUser && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                  Admin View
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Viewing invitations for:{" "}
                  <span className="font-semibold">
                    {viewedUser.name} {viewedUser.surname}
                  </span>{" "}
                  ({viewedUser.email})
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-base-content">
              Coach invitations
            </h2>
            <div className="mt-1">
              <p className="text-sm text-base-content/70">
                {invitations.length === 0
                  ? "No coach invitations yet"
                  : `${invitations.length} coach invitations`}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center px-3 py-2 border border-base-300 rounded-md text-sm font-medium text-base-content/80 bg-base-100 hover:bg-base-200 transition-colors gap-2"
              title="Refresh invitations"
            >
              <svg
                className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {invitations.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-base-200 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-12 h-12 text-base-content/50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-base-content mb-2">
              No invitations
            </h3>
            <p className="text-base-content/70">
              You don't have any coach invitations at the moment.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-0 shadow rounded-lg overflow-hidden">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="bg-base-100 border-b last:border-none p-6"
              >
                <div className="text-sm flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium mb-1">
                      {invitation.coachName} has invited you to join their
                      Coaches Corner
                    </p>
                    {invitation.message && (
                      <p className="text-base-content/70 mb-2 text-sm">
                        "{invitation.message}"
                      </p>
                    )}
                    <div className="flex items-center space-x-1 text-sm">
                      {invitation.status === "pending" && (
                        <div className="py-0.5 px-2 rounded-full text-xs bg-orange-500/10 capitalize text-orange-600 dark:text-orange-400">
                          Pending
                        </div>
                      )}
                      {invitation.status === "accepted" && (
                        <div className="py-0.5 px-2 rounded-full text-xs bg-green-500/10 capitalize text-green-600 dark:text-green-400">
                          Accepted
                        </div>
                      )}
                      {invitation.status === "declined" && (
                        <div className="py-0.5 px-2 rounded-full text-xs bg-red-500/10 capitalize text-red-600 dark:text-red-400">
                          Declined
                        </div>
                      )}
                      {invitation.status === "expired" && (
                        <div className="py-0.5 px-2 rounded-full text-xs bg-base-200 capitalize text-base-content/60">
                          Expired
                        </div>
                      )}
                      <div className="py-0.5 px-2 rounded-full text-xs bg-blue-500/10 capitalize text-blue-600 dark:text-blue-400">
                        Sent: {formatDate(invitation.createdAt)}
                      </div>
                    </div>
                  </div>
                  {invitation.status === "pending" && !viewingAsAdmin && (
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleAcceptInvitation(invitation.id!)}
                        disabled={processingInvitations.has(invitation.id!)}
                        className="px-4 py-2 bg-green-500 rounded-md text-white"
                      >
                        {processingInvitations.has(invitation.id!) ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Accepting...
                          </>
                        ) : (
                          "Accept"
                        )}
                      </button>
                      <button
                        onClick={() => handleDeclineInvitation(invitation.id!)}
                        disabled={processingInvitations.has(invitation.id!)}
                        className="btn btn-sm !bg-red-500/15 !text-red-500 hover:!bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed !border-none !shadow-none"
                      >
                        {processingInvitations.has(invitation.id!) ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-500"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Declining...
                          </>
                        ) : (
                          "Decline"
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Invitations;
